/**
 * Autopilot Inspiration — wrzucanie zewnętrznych inspiracji do pipeline'u.
 *
 * Dwa flowy:
 *   1) Link: TikTok / IG / Meta Ad Library / etc → 1 karta w "pomysly"
 *   2) Screenshot: wgraj screen czyjejś reklamy → AI wygeneruje N wariacji
 *      bazując na firm-assets (Model B) + offer sheet.
 */
import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link2, Image as ImageIcon, Loader2, Sparkles, Plus, X } from "lucide-react";
import { useStore } from "@/lib/store";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import {
  useAddLinkInspiration,
  useAddScreenshotInspiration,
  usePresignInspirationUpload,
} from "@/hooks/useInspiration";
import { useBrandReadiness } from "@/hooks/useFirmAssets";
import { BrandReadinessCheck } from "@/components/autopilot/BrandReadinessCheck";
import { AutopilotPageShell } from "@/components/autopilot/AutopilotPageHeader";

const PLATFORM_HINTS: Array<{ value: string; label: string }> = [
  { value: "auto", label: "Wykryj automatycznie" },
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram / Reels" },
  { value: "meta_ad_library", label: "Meta Ad Library" },
  { value: "facebook", label: "Facebook" },
  { value: "youtube", label: "YouTube" },
  { value: "pinterest", label: "Pinterest" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter_x", label: "X / Twitter" },
  { value: "other", label: "Inne" },
];

export default function AutopilotInspiration() {
  const selectedClientId = useStore((s) => s.selectedClientId);
  const selectedClientName = useStore((s) => s.selectedClientName);
  const scopeKey = selectedClientId ?? null;
  const { toast } = useToast();

  // ── Link form ────────────────────────────────
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState("auto");
  const [linkNotes, setLinkNotes] = useState("");
  const linkMut = useAddLinkInspiration();

  const submitLink = async () => {
    if (!url.trim()) {
      toast({ title: "Wklej URL", variant: "destructive" });
      return;
    }
    try {
      await linkMut.mutateAsync({
        agencyClientId: scopeKey,
        url: url.trim(),
        platform: platform === "auto" ? undefined : platform,
        notes: linkNotes.trim() || undefined,
      });
      toast({ title: "Inspiracja dodana", description: 'Karta w kolumnie "Pomysły"' });
      setUrl("");
      setLinkNotes("");
    } catch (err) {
      toast({
        title: "Błąd",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  // ── Screenshot form ──────────────────────────
  const [variationCount, setVariationCount] = useState(3);
  // Autopilot scope: na razie tylko grafiki. Format zalockowany do 'image'.
  const targetFormat = "image" as const;
  const [preserveAspects, setPreserveAspects] = useState<string[]>([]);
  const [adaptAspects, setAdaptAspects] = useState<string[]>([]);
  const [shotNotes, setShotNotes] = useState("");
  const [newPreserve, setNewPreserve] = useState("");
  const [newAdapt, setNewAdapt] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const presignMut = usePresignInspirationUpload();
  const screenMut = useAddScreenshotInspiration();

  const { data: readiness } = useBrandReadiness(scopeKey);

  const submitScreenshot = async () => {
    if (readiness && !readiness.ready) {
      toast({
        title: "Najpierw dodaj brand assets",
        description: `Brakuje: ${readiness.missing.join(", ")}. Bez tego AI wygeneruje grafiki "z powietrza".`,
        variant: "destructive",
      });
      return;
    }
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast({ title: "Wybierz screen", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, getUrl } = await presignMut.mutateAsync({
        filename: file.name,
        mimeType: file.type || "image/png",
      });
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status}`);
      const result = await screenMut.mutateAsync({
        agencyClientId: scopeKey,
        screenshotUrl: getUrl,
        variationCount,
        targetFormat,
        preserveAspects: preserveAspects.length ? preserveAspects : undefined,
        adaptAspects: adaptAspects.length ? adaptAspects : undefined,
        notes: shotNotes.trim() || undefined,
      });
      toast({ title: `${result.count} wariacji w kolejce`, description: 'Karty w "Pomysły"' });
      if (fileRef.current) fileRef.current.value = "";
      setShotNotes("");
      setPreserveAspects([]);
      setAdaptAspects([]);
    } catch (err) {
      toast({
        title: "Błąd",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const renderChips = (
    items: string[],
    setItems: (v: string[]) => void,
    inputValue: string,
    setInputValue: (v: string) => void,
    placeholder: string
  ) => (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" && inputValue.trim()) {
              e.preventDefault();
              setItems([...items, inputValue.trim()]);
              setInputValue("");
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => {
            if (inputValue.trim()) {
              setItems([...items, inputValue.trim()]);
              setInputValue("");
            }
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {items.map((it, idx) => (
            <Badge key={idx} variant="secondary" className="gap-1">
              {it}
              <button onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <AutopilotPageShell
      title="Inspiracje → Pomysły"
      subtitle={
        <>
          Klient: <Badge variant="secondary">{selectedClientName ?? "Globalna"}</Badge>
          {" · "}Wszystko ląduje w kolumnie "Pomysły" pipeline'u.
        </>
      }
    >
      <Tabs defaultValue="link">
        <TabsList>
          <TabsTrigger value="link">
            <Link2 className="h-4 w-4 mr-2" /> Link
          </TabsTrigger>
          <TabsTrigger value="screenshot">
            <ImageIcon className="h-4 w-4 mr-2" /> Screenshot → wariacje
          </TabsTrigger>
        </TabsList>

        <TabsContent value="link">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Wklej link do reklamy / posta</CardTitle>
              <p className="text-xs text-muted-foreground">
                TikTok, Instagram, Meta Ad Library, YouTube — cokolwiek inspiruje.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>URL</Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.tiktok.com/@..."
                />
              </div>
              <div>
                <Label>Platforma (opcjonalna)</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORM_HINTS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notatki (co Cię inspiruje)</Label>
                <Textarea
                  value={linkNotes}
                  onChange={(e) => setLinkNotes(e.target.value)}
                  placeholder="np. hook w pierwszej sekundzie, kompozycja, kolor..."
                  rows={3}
                />
              </div>
              <Button onClick={submitLink} disabled={linkMut.isPending}>
                {linkMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Dodaj do Pomysłów
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="screenshot" className="space-y-4">
          <BrandReadinessCheck blocking={false} />
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Wgraj screen → AI zrobi wariacje pod nas</CardTitle>
              <p className="text-xs text-muted-foreground">
                AI użyje Twoich firm-assets (logo, produkt, voice) + arkusz ofertowy żeby przerobić
                cudzą reklamę na Twoją. Model B core.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Screenshot</Label>
                <Input ref={fileRef} type="file" accept="image/*" />
              </div>
              <div>
                <Label>Liczba wariacji</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={variationCount}
                  onChange={(e) =>
                    setVariationCount(Math.max(1, Math.min(10, Number(e.target.value) || 3)))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Format: <Badge variant="outline">grafika</Badge> (autopilot na razie tylko
                  graficzne)
                </p>
              </div>
              <div>
                <Label>Co zachować (preserve)</Label>
                {renderChips(
                  preserveAspects,
                  setPreserveAspects,
                  newPreserve,
                  setNewPreserve,
                  "np. kompozycja, kolorystyka, hook"
                )}
              </div>
              <div>
                <Label>Co zaadaptować (adapt na nas)</Label>
                {renderChips(
                  adaptAspects,
                  setAdaptAspects,
                  newAdapt,
                  setNewAdapt,
                  "np. nasze logo, nasz CTA, nasz produkt"
                )}
              </div>
              <div>
                <Label>Notatki dla AI</Label>
                <Textarea
                  value={shotNotes}
                  onChange={(e) => setShotNotes(e.target.value)}
                  placeholder="Dodatkowy kontekst dla AI..."
                  rows={3}
                />
              </div>
              <Button onClick={submitScreenshot} disabled={uploading}>
                {uploading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Wgraj i zleć {variationCount} wariacje
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AutopilotPageShell>
  );
}
